import {
  html,
  attr,
  prop,
  Signal,
  ForEach,
  When,
  on,
  OnDispose,
} from '@tempots/dom'
import { Icon } from '@tempots/beatui'
import type { TNode } from '@tempots/dom'

export interface TagFilterOptions {
  /** All available tags to choose from */
  availableTags: Signal<string[]>
  /** Currently selected tags */
  selectedTags: Signal<string[]>
  /** Optional placeholder text when no tags selected */
  placeholder?: string
  /** Callback when selection changes */
  onChange?: (tags: string[]) => void
}

/**
 * Multi-select tag filter component with dropdown and chips
 *
 * @example
 * TagFilter({
 *   availableTags: allTags,
 *   selectedTags: selectedTags,
 *   onChange: (tags) => selectedTags.set(tags)
 * })
 */
export function TagFilter(options: TagFilterOptions): TNode {
  const {
    availableTags,
    selectedTags,
    placeholder = 'Filter by tags...',
    onChange,
  } = options

  const isOpen = prop(false)

  const toggleTag = (tag: string) => {
    const current = selectedTags.get()
    let newTags: string[]
    if (current.includes(tag)) {
      newTags = current.filter((t) => t !== tag)
    } else {
      newTags = [...current, tag]
    }
    onChange?.(newTags)
  }

  const clearAll = () => {
    onChange?.([])
  }

  const removeTag = (tag: string) => {
    const newTags = selectedTags.get().filter((t) => t !== tag)
    onChange?.(newTags)
  }

  // Close dropdown when clicking outside
  const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target.closest('.tag-filter-container')) {
      isOpen.set(false)
    }
  }

  // Register click outside listener
  if (typeof window !== 'undefined') {
    document.addEventListener('click', handleClickOutside)
    OnDispose(() => {
      document.removeEventListener('click', handleClickOutside)
    })
  }

  return html.div(
    attr.class('tag-filter-container relative w-64'),

    // Selected tags chips + trigger button
    html.div(
      attr.class(
        'flex flex-wrap items-center gap-1 min-h-[42px] px-3 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:border-brand-400 transition-colors'
      ),
      on.click(() => isOpen.set(!isOpen.get())),

      // Selected tag chips
      When(
        selectedTags.map((tags) => tags.length > 0),
        () =>
          ForEach(selectedTags, (tag) =>
            html.span(
              attr.class(
                'inline-flex items-center gap-1 px-2 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded-full'
              ),
              tag,
              html.button(
                attr.type('button'),
                attr.class(
                  'hover:bg-brand-200 rounded-full p-0.5 transition-colors'
                ),
                on.click((e) => {
                  e.stopPropagation()
                  removeTag(tag.get())
                }),
                Icon({ icon: 'mdi:close' }, attr.class('w-3 h-3'))
              )
            )
          ),
        () =>
          html.span(
            attr.class('text-gray-400 text-sm select-none'),
            placeholder
          )
      ),

      // Dropdown arrow
      html.div(
        attr.class('ml-auto pl-2'),
        Icon(
          {
            icon: isOpen.map((open) =>
              open ? 'mdi:chevron-up' : 'mdi:chevron-down'
            ) as Signal<string>,
          },
          attr.class('w-5 h-5 text-gray-400')
        )
      )
    ),

    // Dropdown menu
    When(isOpen, () =>
      html.div(
        attr.class(
          'absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto'
        ),

        // Clear all button
        When(
          selectedTags.map((tags) => tags.length > 0),
          () =>
            html.button(
              attr.type('button'),
              attr.class(
                'w-full px-3 py-2 text-left text-sm text-brand-600 hover:bg-gray-50 border-b border-gray-100'
              ),
              on.click((e) => {
                e.stopPropagation()
                clearAll()
              }),
              'Clear all'
            )
        ),

        // Tag options
        ForEach(availableTags, (tag) =>
          html.label(
            attr.class(
              'flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer'
            ),
            html.input(
              attr.type('checkbox'),
              attr.class(
                'w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500'
              ),
              attr.checked(
                selectedTags.map((tags) => tags.includes(tag.get()))
              ),
              on.change(() => toggleTag(tag.get()))
            ),
            html.span(
              attr.class('text-sm text-gray-700'),
              tag.map((t) => formatTagName(t))
            )
          )
        ),

        // Empty state
        When(
          availableTags.map((tags) => tags.length === 0),
          () =>
            html.div(
              attr.class('px-3 py-4 text-sm text-gray-500 text-center'),
              'No tags available'
            )
        )
      )
    )
  )
}

/**
 * Format a tag name for display (capitalize first letter)
 */
function formatTagName(tag: string): string {
  return tag.charAt(0).toUpperCase() + tag.slice(1)
}

/**
 * Simple tag badge for display
 */
export function TagBadge(tag: Signal<string>): TNode {
  return html.span(
    attr.class(
      'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600'
    ),
    tag.map((t) => formatTagName(t))
  )
}
